import {
    PrPayslipEmploymentItemTypeCode,
    PrPayslipEmploymentSubitemTypeCode,
    PrPayslipItemTypeCode,
    PrPayslipSubitemTypeCode
} from "@odata/GeneratedEnums";
import {
    RowValueType,
    TPayslipItem,
    TPayslipOverviewRowDef,
    TPayslipSubItemDef
} from "@pages/payroll/payslip/overview/TotalOverview.utils";
import i18next from "i18next";
import { IPrPayslipEmploymentItemEntity } from "@odata/GeneratedEntityTypes";


interface IPayslipOverviewSectionsDef {
    title?: string;
    rows: TPayslipOverviewRowDef[];
}

function getRowHeaderLabel(key: string): string {
    return `${i18next.t(`Payslip:FormOverviews.${key}`).toString()}:`;
}

function getHeaderRowDef(additionalLabels: string[], visibilityCondition?: string[]): TPayslipOverviewRowDef {
    return {
        label: getRowHeaderLabel("Name"),
        value: getRowHeaderLabel("Summary"),
        isHeader: true,
        isSubRow: true,
        subItems: additionalLabels.map((key): TPayslipSubItemDef => ({ value: getRowHeaderLabel(key) })),
        visibleIfContains: visibilityCondition
    };
}

/**
 * Configuration for basic payslip calculations block related to employee
 */
export const getTotalOverviewPayslipSectionsDef: () => IPayslipOverviewSectionsDef[] = () => ([
    {
        rows: [
            // **** Benefits ****
            { id: PrPayslipItemTypeCode.BenefitsTotal, isHeader: true },
            {
                id: [
                    PrPayslipItemTypeCode.PensionInsuranceBenefit,
                    PrPayslipItemTypeCode.LifeInsuranceBenefit,
                    PrPayslipItemTypeCode.DIPBenefit,
                ]
            },
            { id: PrPayslipItemTypeCode.TaxdeductibleBenefitsTotal },

            // **** Basic calculations block ****
            { id: PrPayslipItemTypeCode.TaxableIncomeTotal, isHeader: true },
            { id: PrPayslipItemTypeCode.IncomeTaxCalculationBasis },
            { id: PrPayslipItemTypeCode.IncomeTaxCalculationBasisRounded },
            { id: PrPayslipItemTypeCode.IncomeTaxBeforeAdvantages },
            { id: PrPayslipItemTypeCode.IncomeTaxReductions },
            { id: PrPayslipItemTypeCode.IncomeTaxAdvantages },
            { id: PrPayslipItemTypeCode.IncomeTaxNet },
            { id: PrPayslipItemTypeCode.IncomeTaxBonus },

            // **** Health Insurance ****
            { id: PrPayslipItemTypeCode.HealthInsuranceTotal, isHeader: true },
            getHeaderRowDef(["Size"], [PrPayslipItemTypeCode.HealthInsurancePaidByEmployee, PrPayslipItemTypeCode.HealthInsurancePaidByEmployer]),
            { id: PrPayslipItemTypeCode.BasisForHealthInsuranceCalculation, isSubRow: true },
            {
                id: PrPayslipItemTypeCode.HealthInsurancePaidByEmployee,
                subItems: [
                    { id: PrPayslipSubitemTypeCode.HealthInsuranceEmployeePercentage, valueType: RowValueType.Percentage }
                ]
            },
            {
                id: PrPayslipItemTypeCode.HealthInsurancePaidByEmployer,
                subItems: [
                    { id: PrPayslipSubitemTypeCode.HealthInsuranceEmployerPercentage, valueType: RowValueType.Percentage }
                ]
            },

            // **** Social Insurance ****
            { id: PrPayslipItemTypeCode.SocialInsuranceTotal, isHeader: true },
            getHeaderRowDef(["Size"], [PrPayslipItemTypeCode.SocialInsurancePaidByEmployee, PrPayslipItemTypeCode.SocialInsurancePaidByEmployer]),
            { id: PrPayslipItemTypeCode.BasisForSocialInsuranceCalculation, isSubRow: true },
            {
                id: PrPayslipItemTypeCode.SocialInsurancePaidByEmployee,
                subItems: [
                    { id: PrPayslipSubitemTypeCode.SocialInsuranceEmployeePercentage, valueType: RowValueType.Percentage }
                ]
            },
            {
                id: PrPayslipItemTypeCode.SocialInsurancePaidByEmployer,
                subItems: [
                    { id: PrPayslipSubitemTypeCode.SocialInsuranceEmployerPercentage, valueType: RowValueType.Percentage }
                ]
            },

            // **** Net Salary ****
            { id: PrPayslipItemTypeCode.NetSalary, isHeader: true },

            // **** Deductions ****
            { id: PrPayslipItemTypeCode.DeductionTotal, isHeader: true },
            { id: PrPayslipItemTypeCode.CustomDeduction },
            { id: PrPayslipItemTypeCode.OwnDeduction },
            { id: PrPayslipItemTypeCode.CourtOrderedDeduction },

            // **** Liabilities and Receivables ****
            { id: PrPayslipItemTypeCode.LiabilitiesTotal, isHeader: true },
            { id: PrPayslipItemTypeCode.Liabilities },
            { id: PrPayslipItemTypeCode.ReceivablesTotal, isHeader: true },
            { id: PrPayslipItemTypeCode.Receivables },
        ]
    },
    {
        rows: [
            // **** Amount to pay employee ****
            { id: PrPayslipItemTypeCode.AmountToPayEmployee, isHeader: true }
        ]
    },
]);

const vacationBalanceFormatter = (item: TPayslipItem, valueFormatter: (value: number) => string) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    const used = empItem.IntValueOverride ?? empItem.IntValue;
    const previous = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.VacationPreviouslyUsed)?.IntValue ?? 0;
    const entitlement = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.VacationEntitlement)?.IntValue ?? 0;
    const calc = entitlement - previous - used;
    return valueFormatter ? valueFormatter(calc) : calc.toString();
};

const sickdayBalanceFormatter = (item: TPayslipItem, valueFormatter: (value: number) => string) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    const used = empItem.IntValueOverride ?? empItem.IntValue;
    const previous = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.SickDayPreviouslyUsed)?.IntValue ?? 0;
    const entitlement = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.SickDayEntitlement)?.IntValue ?? 0;
    const calc = entitlement - previous - used;
    return valueFormatter ? valueFormatter(calc) : calc.toString();
};

/**
 * Configuration for basic payslip calculations block related to employment
 */
export const getTotalOverviewEmploymentSectionsDef: () => IPayslipOverviewSectionsDef = () => (
        {
            rows: [
                // **** Overview ****
                { id: PrPayslipEmploymentItemTypeCode.GrossSalaryEmployment, isHeader: true },
                { id: PrPayslipEmploymentItemTypeCode.PlannedWorkHours, valueType: RowValueType.HoursFromMinutes },
                { id: PrPayslipEmploymentItemTypeCode.DaysWorked, valueType: RowValueType.Days },
                { id: PrPayslipEmploymentItemTypeCode.HoursWorked, valueType: RowValueType.HoursFromMinutes },
                // **** Salary Components ****
                { id: PrPayslipEmploymentItemTypeCode.GrossSalaryFullyTaxed, isHeader: true },
                {
                    id: [
                        PrPayslipEmploymentItemTypeCode.SalaryForHoursWorked,
                        PrPayslipEmploymentItemTypeCode.SalaryPercentageBonus,
                        PrPayslipEmploymentItemTypeCode.SalaryBonus,
                        PrPayslipEmploymentItemTypeCode.OtherSalaryFullyTaxed,
                        PrPayslipEmploymentItemTypeCode.SalaryInKindFullyTaxed
                    ],
                    headerRow: getHeaderRowDef(["Size"/*, "Hours"*/]),
                    isSubRow: true,
                    subItems: [
                        { id: PrPayslipEmploymentSubitemTypeCode.SalaryForHoursWorkedBasis }
                    ]
                },
                // **** Salary Components outside Insurance ****
                { id: PrPayslipEmploymentItemTypeCode.GrossSalaryIncomeTaxOnly, isHeader: true },
                {
                    id: [
                        PrPayslipEmploymentItemTypeCode.SalaryInKindIncomeTaxOnly,
                        PrPayslipEmploymentItemTypeCode.OtherSalaryIncomeTaxOnly
                    ],
                    headerRow: getHeaderRowDef(["Size"]),
                    isSubRow: true,
                },
                // **** Absences ****
                { id: PrPayslipEmploymentItemTypeCode.AbsenceTotal, isHeader: true },
                getHeaderRowDef(["Entitlement", "Previous", "Balance"], [PrPayslipEmploymentItemTypeCode.Vacation, PrPayslipEmploymentItemTypeCode.CustomAbsence]),
                {
                    id: PrPayslipEmploymentItemTypeCode.Vacation,
                    isSubRow: true,
                    valueType: RowValueType.HoursFromMinutes,
                    subItems: [
                        { id: PrPayslipEmploymentSubitemTypeCode.VacationEntitlement, valueType: RowValueType.HoursFromMinutes },
                        { id: PrPayslipEmploymentSubitemTypeCode.VacationPreviouslyUsed, valueType: RowValueType.HoursFromMinutes },
                        { formatter: vacationBalanceFormatter, valueType: RowValueType.HoursFromMinutes }
                    ]
                },
                {
                    id: PrPayslipEmploymentItemTypeCode.SickDay,
                    isSubRow: true,
                    valueType: RowValueType.Days,
                    subItems: [
                        { id: PrPayslipEmploymentSubitemTypeCode.SickDayEntitlement, valueType: RowValueType.Days },
                        { id: PrPayslipEmploymentSubitemTypeCode.SickDayPreviouslyUsed, valueType: RowValueType.Days },
                        { formatter: sickdayBalanceFormatter, valueType: RowValueType.Days }
                    ]
                },
                {
                    id: [PrPayslipEmploymentItemTypeCode.CustomAbsence, PrPayslipEmploymentItemTypeCode.LegislativeAbsencePaid, PrPayslipEmploymentItemTypeCode.Illness, PrPayslipEmploymentItemTypeCode.LegislativeAbsenceUnpaid],
                    isSubRow: true, valueType: RowValueType.HoursFromMinutes
                },
                {
                    id: PrPayslipEmploymentItemTypeCode.AverageSalary,
                    isSubRow: true,
                    valueType: RowValueType.CurrencyPerHour
                },
                // **** Extra Salaries ****
                { id: PrPayslipEmploymentItemTypeCode.ExtraSalaryTotal, isHeader: true },
                {
                    id: [
                        PrPayslipEmploymentItemTypeCode.Night,
                        PrPayslipEmploymentItemTypeCode.Weekend,
                        PrPayslipEmploymentItemTypeCode.Overtime,
                        PrPayslipEmploymentItemTypeCode.Holiday,
                    ],
                    valueType: RowValueType.Hours
                },
            ]
        }
);